import {
  Checkbox,
  Grid,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
// Icons
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import { Searcher } from 'fast-fuzzy';
import { cloneDeep } from 'lodash-es';
import React, { FunctionComponent, KeyboardEvent, useEffect, useState } from 'react';

import { IChannelGroup, IChannelLabel } from '~/src/dash/api/console-api';
import { IQuickFilter } from '~/src/dash/components/material-search-dropdown-multi-list';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { obfuscateChannelGroupName, obfuscateChannelLabels } from '~/src/dash/utils/obfuscate';

export const useStyles = makeStyles({
  customWidth: {
    '& div': {
      width: 450,
    },
  },
  textField: {
    '& div': {
      backgroundColor: '#e8edf4',
      width: 460,
      margin: 10,
    },
    '& input': {
      padding: 10,
    },
  },
  textFieldStatic: {
    '& div': {
      backgroundColor: '#e8edf4',
      height: '100%',
      width: '100%',
      maxWidth: '500px',
      margin: 10,
    },
    '& input': {
      padding: 10,
      width: '100%',
    },
  },
  menuItem: {
    padding: 12,
    maxHeight: 40,
  },
  selectCancel: {
    fontSize: 14,
    fontWeight: 500,
    color: getColorTheme('default'), // ???
    maxWidth: 80,
    padding: 10,
    cursor: 'pointer',
  },
  menuOpen: {
    width: '100%',
    height: '100%',
    visibility: 'visible',
  },
  menuClosed: {
    visibility: 'hidden',
  },
  filterOptionButtons: {
    padding: 12,
    cursor: 'pointer',
    background: 'white',
    '&:hover': {
      background: 'rgba(196, 196, 196, 0.24)',
    },
  },
  filterOptionButtonsSelected: {
    padding: 12,
    cursor: 'pointer',
    background: 'rgba(196, 196, 196, 0.14)',
    '&:hover': {
      background: 'rgba(196, 196, 196, 0.34)',
    },
  },
  menuHeader: {
    margin: 10,
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    color: '#A9A9A9',
  },
});

// genQuickFilter will get the filter options for component (such as Bidder Settings)'s state (i.e. Groups and Labels)
export const genQuickFilter = (
  channelGroups: IChannelGroup[],
  channelLabels: IChannelLabel[],
  username: string,
) => {
  const groupsFilter = {
    title: 'Group',
    optionItems: {} as IMenuList,
    type: 'Channel',
  };
  for (let i = 0; i < channelGroups.length; i++) {
    const optionItem = {
      title: obfuscateChannelGroupName(
        channelGroups[i],
        username,
        `[${channelGroups[i].ID}] ${channelGroups[i].Name}`,
      ),
      ID: channelGroups[i].ID.toString(),
      siteIDs: channelGroups[i].ChannelIDs.split(','),
      selected: false,
    };
    groupsFilter.optionItems[channelGroups[i].ID] = optionItem;
  }

  const labelFilter = {
    title: 'Label',
    optionItems: {} as IMenuList,
    type: 'Channel',
  };
  for (let i = 0; i < channelLabels.length; i++) {
    const siteIDs = [] as string[];
    channelLabels[i].Sites.forEach((site: { ID: { toString: () => string } }) => {
      siteIDs.push(site.ID.toString());
    });
    const optionItem = {
      title: obfuscateChannelLabels(
        channelLabels[i],
        username,
        `[${channelLabels[i].ID}] ${channelLabels[i].Name}`,
      ),
      ID: channelLabels[i].ID.toString(),
      siteIDs,
      selected: false,
    };
    labelFilter.optionItems[channelLabels[i].ID] = optionItem;
  }

  return [groupsFilter, labelFilter];
};

// activeQuickFilterOptions contain the filter options for Active/Inactive/Show All
export const activeQuickFilterOptions = [
  {
    title: 'Active',
    optionItems: {} as IMenuList,
    type: 'Active',
  },
  {
    title: 'Inactive',
    optionItems: {} as IMenuList,
    type: 'Active',
  },
  {
    title: 'Show All',
    optionItems: {} as IMenuList,
    type: 'Active',
  },
];

interface IPropsQuickFilter {
  // SEARCH PROPS
  //
  // Function to get the value from a search element. Used for the search
  // default: take the value from the title field
  getValueFromSearchElement?: (src: any) => string;

  menuTitle: string;
  // Input list
  list: IMenuList;
  // Is MultiSelect allowed?
  // default=true
  enableMultiSelect?: boolean;
  // On change, input is modified list (with .selected set and up to date)
  onChangeMainDropdown: (values: IMenuList) => void;

  handleQFDropdownAccept: (listOverride?: IMenuList) => void;

  handleClickOutside: (this: Document, e: MouseEvent) => void;

  // quick filter
  quickFilterItemsList?: IMenuList;
  quickFilterMenuItems: IQuickFilter[];
  onChangeQuickFilter?: (
    values: Array<{ title: string; optionItems: IMenuList; type: string }>,
  ) => void;
  setFilterOptionItems: React.Dispatch<React.SetStateAction<IQuickFilter[]>>;

  // limits the number of selections that can be made on the menu
  selectLimit?: number;

  // Loading material search
  loading?: boolean;
}

export const QuickFilterDropdown: FunctionComponent<IPropsQuickFilter> = (
  props: IPropsQuickFilter,
) => {
  const {
    onChangeMainDropdown,
    menuTitle,
    list,
    loading,
    enableMultiSelect,
    quickFilterItemsList,
    handleQFDropdownAccept,
    handleClickOutside,
    quickFilterMenuItems,
    onChangeQuickFilter,
    setFilterOptionItems,
  } = props;

  // State
  const classes = useStyles();
  const [searchInput, setSearchInput] = useState<string>('');
  const [fuzzySearcher, setFuzzySearcher] = useState<Searcher<IListItem, any>>(undefined as any);

  useEffect(() => {
    setSearchInput('');

    if (quickFilterItemsList !== undefined) {
      const fuzzyV2 = new Searcher(Object.values(quickFilterItemsList), {
        keySelector: (obj) => {
          let selector = '';
          if (obj.ID !== undefined) {
            selector += obj.ID;
          }
          selector += obj.title;
          return selector;
        },
        threshold: 0.8,
      });

      setFuzzySearcher(fuzzyV2);
    }
  }, [menuTitle]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  });

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchInput(e.currentTarget.value);
  };

  // Adds/removes selected from item in hash then sets state in parent component
  const handleItemClick = (title: string, id: string): void => {
    const newFilterItemsList = cloneDeep(quickFilterItemsList);
    const newList = cloneDeep(list);
    for (let i = 0; i < Object.values(newList).length; i++) {
      newList[Object.values(newList)[i].ID].selected = false;
    }

    if (newFilterItemsList === undefined) {
      return;
    }

    if (props.enableMultiSelect === false) {
      // Unselect everything.
      for (const obj of Object.values(newFilterItemsList)) {
        newFilterItemsList[obj.ID].selected = false;
      }
      newFilterItemsList[id].selected = true;

      // Accept
      handleQFDropdownAccept(newFilterItemsList);
    } else {
      let allowSelect = true;
      if (props.selectLimit && props.selectLimit !== -1) {
        let selectedCount = 0;
        for (const obj of Object.values(newFilterItemsList)) {
          if (newFilterItemsList[obj.ID].selected) {
            selectedCount++;
            if (selectedCount === props.selectLimit) {
              allowSelect = false;
              break;
            }
          }
        }
      }
      if (allowSelect || newFilterItemsList[id].selected) {
        newFilterItemsList[id].selected = !newFilterItemsList[id].selected;
      }
    }
    const newQuickFilterList = [] as Array<{ title: string; optionItems: IMenuList; type: string }>;
    const newFilterOptionItems = [] as IQuickFilter[];

    [...quickFilterMenuItems].forEach((filterOption) => {
      const quickFilterListItem = {
        title: filterOption.title,
        optionItems: filterOption.optionItems,
        type: filterOption.type,
      };
      const filterOptionItem = {
        title: filterOption.title,
        optionItems: filterOption.optionItems,
        show: filterOption.show,
        type: filterOption.type,
      };

      const unselectedFilterOption = filterOption.optionItems;
      Object.values(unselectedFilterOption).forEach((item) => {
        unselectedFilterOption[item.ID].selected = false;
      });

      if (filterOption.title === title) {
        quickFilterListItem.optionItems = newFilterItemsList;
        filterOptionItem.optionItems = newFilterItemsList;
      } else {
        quickFilterListItem.optionItems = filterOption.optionItems;
      }
      newQuickFilterList.push(quickFilterListItem);
      newFilterOptionItems.push(filterOptionItem);
    });

    if (onChangeQuickFilter) {
      onChangeQuickFilter(newQuickFilterList);
    }
    setFilterOptionItems(newFilterOptionItems);

    for (let i = 0; i < Object.values(newFilterItemsList).length; i++) {
      if (Object.values(newFilterItemsList)[i].selected) {
        const siteIDs = Object.values(newFilterItemsList)[i].siteIDs;
        if (siteIDs !== undefined) {
          for (let j = 0; j < siteIDs.length; j++) {
            if (newList[siteIDs[j]] !== undefined) {
              newList[siteIDs[j]].selected = true;
            }
          }
        }
      }
    }

    onChangeMainDropdown(newList);
  };

  if (quickFilterItemsList && Object.values(quickFilterItemsList).length === 0) {
    return (
      <Paper
        elevation={0}
        style={{
          position: 'absolute',
          zIndex: 999,
          backgroundColor: 'white',
          minWidth: 140,
          marginTop: -40,
          marginLeft: 135,
          boxShadow: '0px 3px 4px #E2E2E2',
        }}
      >
        <MenuItem className={classes.menuItem}>
          <Typography>There is no {menuTitle} here.</Typography>
        </MenuItem>
      </Paper>
    );
  }

  function createFilteredList(): IListItem[] {
    if (quickFilterItemsList === undefined) {
      return [] as IListItem[];
    }
    if (searchInput !== '') {
      if (fuzzySearcher === undefined) {
        const fuzzyV2 = new Searcher(Object.values(quickFilterItemsList), {
          keySelector: (obj) => {
            let selector = '';
            if (obj.ID !== undefined) {
              selector += obj.ID;
            }
            selector += obj.title;
            return selector;
          },
          threshold: 0.8,
        });

        setFuzzySearcher(fuzzyV2);
      }
      const searchResult = fuzzySearcher.search(searchInput);
      return Object.values(searchResult);
    } else {
      return Object.values(quickFilterItemsList);
    }
  }

  function sortFilteredList(filteredList: IListItem[]) {
    // only sort by selected to move selected to the top
    // otherwise sorting is done by ranking fuzzy searching
    const selectedItems = filteredList.filter((item: IListItem) => item && item.selected);
    const notSelectedItems = filteredList.filter((item: IListItem) => item && !item.selected);
    return [...selectedItems, ...notSelectedItems];
  }

  const filteredList = createFilteredList();
  let sortedList = [] as IListItem[];
  if (quickFilterItemsList && filteredList) {
    sortedList = sortFilteredList(Object.values(filteredList));
  }

  return (
    <Paper
      elevation={0}
      style={{
        position: 'absolute',
        zIndex: 999,
        backgroundColor: 'white',
        maxWidth: 420,
        minWidth: 140,
        marginTop: -40,
        marginLeft: 135,
        boxShadow: '0px 3px 4px #E2E2E2',
      }}
    >
      <Grid>
        <Grid item style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          <TextField
            autoFocus
            disabled={loading}
            onChange={handleTextInput}
            value={searchInput}
            variant='filled'
            className={classes.textField}
            placeholder={enableMultiSelect ? `Search ${menuTitle}` : `Search for ${menuTitle}`}
            InputProps={{
              disableUnderline: true,
              style: { width: 400, borderRadius: 4 },
              endAdornment: (
                <IconButton size='small' onClick={() => setSearchInput('')}>
                  <ClearIcon />
                </IconButton>
              ),
            }}
            inputRef={(input) => input && input.focus()}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              e.stopPropagation();
            }}
          />
        </Grid>
        <MenuList style={{ maxHeight: 350, overflowY: 'scroll', height: '96%' }}>
          {props.loading ? (
            <MenuItem className={classes.menuItem}>
              <Typography>Loading...</Typography>
            </MenuItem>
          ) : // no results are found when searching
          sortedList.length === 0 && searchInput.length > 0 ? (
            <MenuItem className={classes.menuItem}>
              <Typography
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                No Results
              </Typography>
            </MenuItem>
          ) : (
            sortedList.map((item: IListItem) => {
              if (item === undefined) {
                return <></>;
              }
              const { title, selected, ID, siteIDs } = item;
              let activeSiteIDs = 0;
              if (siteIDs !== undefined) {
                siteIDs.forEach((siteID) => {
                  if (list[siteID] !== undefined) {
                    activeSiteIDs++;
                  }
                });
              }
              return (
                <Tooltip title={`${title} : ${activeSiteIDs} active channels`} key={ID}>
                  <MenuItem
                    className={classes.menuItem}
                    style={{ maxHeight: 35 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(menuTitle, ID);
                    }}
                  >
                    {props.enableMultiSelect === true || selected ? (
                      <Checkbox checked={selected} />
                    ) : (
                      <Checkbox style={{ visibility: 'hidden' }} />
                    )}
                    <Typography
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.875rem',
                      }}
                    >
                      {title}
                    </Typography>
                  </MenuItem>
                </Tooltip>
              );
            })
          )}
        </MenuList>
      </Grid>
    </Paper>
  );
};
